import axios from "axios";

import {
  acquireCSRFToken,
  defaultApiUrl,
  API_VERSION,
} from "../../../libraries/network.implementation";

export type UploadRequestResult =
  | { kind: "accepted"; receiptId: string }
  | { kind: "cancelled" }
  | { kind: "throttled"; retryAfterSeconds: number }
  | { kind: "failed"; errorCode?: string };

type UploadRequestOptions = {
  signal: AbortSignal;
};

type UploadResponse = { uuid: string; status: string };

const RECEIPTS_ENDPOINT = `${defaultApiUrl}/${API_VERSION}/receipts/`;
const DEFAULT_RETRY_AFTER_SECONDS = 60;

const NON_RETRYABLE_ERROR_CODES: ReadonlySet<string> = new Set([
  "file_too_large",
  "unsupported_mime_type",
  "corrupt_file",
  "virus_detected",
  "too_many_pages",
]);

export const isRetryableUploadError = (errorCode: string | undefined) =>
  errorCode === undefined || !NON_RETRYABLE_ERROR_CODES.has(errorCode);

const readResponse = (error: unknown) => {
  if (typeof error !== "object" || error === null) return undefined;
  const { response } = error as {
    response?: {
      status?: number;
      headers?: Record<string, unknown>;
      data?: unknown;
    };
  };
  return response;
};

const readErrorCode = (data: unknown): string | undefined => {
  if (typeof data !== "object" || data === null) return undefined;
  const { id } = data as { id?: unknown };
  return typeof id === "string" ? id : undefined;
};

const readRetryAfterSeconds = (
  headers: Record<string, unknown> | undefined
) => {
  const raw = headers?.["retry-after"];
  const seconds = Number(raw);
  return Number.isFinite(seconds) && seconds > 0
    ? seconds
    : DEFAULT_RETRY_AFTER_SECONDS;
};

export const classifyUploadError = (error: unknown): UploadRequestResult => {
  if (axios.isCancel?.(error)) return { kind: "cancelled" };
  const response = readResponse(error);
  const errorCode = readErrorCode(response?.data);
  if (response?.status === 429 || errorCode === "throttled") {
    return {
      kind: "throttled",
      retryAfterSeconds: readRetryAfterSeconds(response?.headers),
    };
  }
  return { kind: "failed", errorCode };
};

export const uploadReceiptFile = async (
  file: File,
  { signal }: UploadRequestOptions
): Promise<UploadRequestResult> => {
  const formData = new FormData();
  formData.append("file", file);
  try {
    // Multipart bypasses the shared network wrapper, so the CSRF token is attached by hand.
    const csrfToken = await acquireCSRFToken();
    const result = await axios.post<UploadResponse>(
      RECEIPTS_ENDPOINT,
      formData,
      {
        signal,
        headers: { Accept: "application/json", "x-csrftoken": csrfToken },
        withCredentials: true,
      }
    );
    const receiptId = result.data?.uuid;
    if (typeof receiptId !== "string") return { kind: "failed" };
    return { kind: "accepted", receiptId };
  } catch (error) {
    return classifyUploadError(error);
  }
};

export const deleteReceipt = async (receiptId: string): Promise<boolean> => {
  try {
    const csrfToken = await acquireCSRFToken();
    await axios.delete(`${RECEIPTS_ENDPOINT}${receiptId}/`, {
      headers: { Accept: "application/json", "x-csrftoken": csrfToken },
      withCredentials: true,
    });
    return true;
  } catch {
    return false;
  }
};
